/** @jsx jsx */
import { useCallback, useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { Authorize } from '@erecruit/modules';
import { AppPermissions } from '@erecruit/types';
import {
  CardOverview,
  Container,
  styled,
  H2,
  Flex,
  FlexItem,
  jsx,
  Accordion,
  Spinner,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import {
  GeneralSection,
  LocationSection,
  ScheduleSection,
  BillingSection,
  AdditionalInfoSection,
} from './sections';
import { getModalConfigForSection } from '../helpers';
import {
  approveApprovalPath,
  fetchPositionRecord,
  rejectApprovalPath,
  savePositionRecord,
  selectors,
} from '../state/reducer';
import { RootState } from '../../../redux/reducers';
import { getLoggedUserGuid, getLoggedUserId } from '../../../App/selectors';
import { ApprovalPath } from '../../ApprovalPath';

import {
  ModalConfig,
  PositionSection,
  PositionRecordSections,
  PositionRecordMatchCounters,
  PositionRecordMutation,
  PositionRecordParams,
  PositionInfoSections,
} from '../types';
import EditPositionModal from './EditPositionModal';
import Progress from './Progress';

const { FormattedMessage } = Intl;

const DetailsContainer = styled(Container)({
  margin: '-120px 16px 0px 16px!important',
  background: 'transparent',
});

const AccordionContainer = styled.div(({ theme: { newColors } }) => ({
  background: newColors.bgPagePrimary,
  padding: '16px 16px 156px',
  marginRight: 0,
}));

const ApprovalPathContainer = styled(Flex)({
  marginTop: 16,
});

const SpinnerContainer = styled(Container)({
  padding: '300px 0',
  textAlign: 'center',
});

export interface PositionDetailsProps {
  approveApprovalPath: typeof approveApprovalPath;
  counters?: PositionRecordMatchCounters;
  fetchPositionRecord: typeof fetchPositionRecord;
  isLoading: boolean;
  rejectApprovalPath: typeof rejectApprovalPath;
  savePositionRecord: typeof savePositionRecord;
  sections?: PositionRecordSections;
  userGuid: string;
  positionRecordInfo?: PositionInfoSections;
  contactId: number;
}

export const PositionDetails = ({
  approveApprovalPath,
  counters,
  fetchPositionRecord,
  isLoading,
  rejectApprovalPath,
  savePositionRecord,
  sections,
  userGuid,
  positionRecordInfo,
  contactId,
}: PositionDetailsProps) => {
  const routeMatch = useRouteMatch<PositionRecordParams>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalConfig, setEditModalConfig] = useState<
    ModalConfig | undefined
  >();
  const isMyOwn = positionRecordInfo?.aboutPosition.contact.id === contactId;

  useEffect(() => {
    fetchPositionRecord(Number(routeMatch.params.id));
  }, [fetchPositionRecord, routeMatch.params.id]);

  const handleSavePosition = useCallback(
    (values: PositionRecordMutation) => {
      savePositionRecord(values);
      setEditModalVisible(false);
    },
    [savePositionRecord],
  );

  const handleEdit = useCallback(
    (section: PositionSection) => {
      if (!sections) return;

      const data = sections[section];
      const modalConfig = getModalConfigForSection(section, data);

      setEditModalConfig(modalConfig);
      setEditModalVisible(true);
    },
    [sections],
  );

  if (isLoading)
    return (
      <SpinnerContainer gutterDirection="vertical" background="pageSecondary">
        <Spinner />
      </SpinnerContainer>
    );

  if (!sections || !counters) return null;

  const progress = (
    <Progress
      counters={counters}
      numberOfPositions={sections.aboutPosition.numberOfPositions || 0}
    />
  );

  return (
    <Fragment>
      {editModalConfig ? (
        <EditPositionModal
          visible={editModalVisible}
          config={editModalConfig}
          handleToggle={() => setEditModalVisible(false)}
          onSavePosition={handleSavePosition}
          sections={sections}
          additionalInfo={sections.additionalInfo}
        />
      ) : null}
      <Container gutter={4} background="pageSecondary">
        {sections.approvalPath ? (
          <AccordionContainer>
            <Accordion
              defaultItem={
                <ApprovalPathContainer>
                  <ApprovalPath
                    approvalPath={sections.approvalPath}
                    userGuid={userGuid}
                    onApproveClick={(approvalPathId, note) =>
                      approveApprovalPath({ approvalPathId, note })
                    }
                    onRejectClick={(approvalPathId, rejectionReasonId, note) =>
                      rejectApprovalPath({
                        approvalPathId,
                        note,
                        rejectionReasonId,
                      })
                    }
                    rejectionReasons={sections.approvalRejectionReasons}
                  />
                </ApprovalPathContainer>
              }
              expandedItem={progress}
              buttonText={(collapsed: boolean) =>
                collapsed ? (
                  <FormattedMessage
                    id={messageIds.vms.positionRecord.accordionCollapsedText}
                  />
                ) : (
                  <FormattedMessage
                    id={messageIds.vms.positionRecord.accordionExpandedText}
                  />
                )
              }
            />
          </AccordionContainer>
        ) : (
          progress
        )}
        <DetailsContainer padding={[0, 12, 12, 12]} gutter={4} stretch>
          <CardOverview
            title={
              <H2 color="headerPrimary">
                <FormattedMessage
                  id={messageIds.vms.positionRecord.sectionsTitle}
                />
              </H2>
            }
            showLessText={
              <FormattedMessage
                id={messageIds.vms.positionRecord.showLessText}
              />
            }
            showMoreText={
              <FormattedMessage
                id={messageIds.vms.positionRecord.showMoreText}
              />
            }
          >
            {expanded => (
              <Flex gutter={4}>
                <FlexItem basis="50%">
                  <Flex
                    direction="column"
                    gutter={4}
                    gutterDirection="vertical"
                  >
                    <GeneralSection
                      data={sections.aboutPosition}
                      expanded={expanded}
                      onEdit={handleEdit}
                      isMyOwn={isMyOwn}
                    />
                    <Authorize permission={AppPermissions.ViewPositionSchedule}>
                      <ScheduleSection
                        data={sections.schedule}
                        expanded={expanded}
                        onEdit={handleEdit}
                        isMyOwn={isMyOwn}
                      />
                    </Authorize>
                  </Flex>
                </FlexItem>
                <FlexItem basis="50%">
                  <Flex
                    direction="column"
                    gutter={4}
                    gutterDirection="vertical"
                  >
                    <Authorize permission={AppPermissions.ViewPositionLocation}>
                      <LocationSection
                        data={sections.location}
                        expanded={expanded}
                        onEdit={handleEdit}
                        isMyOwn={isMyOwn}
                      />
                    </Authorize>
                    <Authorize permission={AppPermissions.ViewPositionBilling}>
                      <BillingSection
                        data={sections.billing}
                        expanded={expanded}
                        isMyOwn={isMyOwn}
                        onEdit={handleEdit}
                      />
                    </Authorize>
                    {sections.additionalInfo.length > 0 && (
                      <Authorize
                        permission={AppPermissions.ViewPositionAdditionalInfo}
                      >
                        <AdditionalInfoSection
                          data={sections.additionalInfo}
                          expanded={expanded}
                          isMyOwn={isMyOwn}
                          onEdit={handleEdit}
                        />
                      </Authorize>
                    )}
                  </Flex>
                </FlexItem>
              </Flex>
            )}
          </CardOverview>
        </DetailsContainer>
      </Container>
    </Fragment>
  );
};

export default connect(
  (state: RootState) => ({
    sections: selectors.selectPositionRecordState(state).sections,
    counters: selectors.selectPositionRecordState(state).counters,
    positionRecordInfo: selectors.selectPositionRecordState(state).infoSections,
    isLoading: selectors.selectPositionRecordState(state).isLoading,
    userGuid: getLoggedUserGuid(state),
    contactId: getLoggedUserId(state),
  }),
  {
    approveApprovalPath,
    fetchPositionRecord,
    rejectApprovalPath,
    savePositionRecord,
  },
)(PositionDetails);
